/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import SymbolSet from '../Symbol/SymbolSet';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import ReferencedComponent from '../Utility/Component/ComponentReference/ReferencedComponent';
import ObservableColorComponent from '../Utility/Component/Observable/ObservableColorComponent';
import ObservableStringComponent from '../Utility/Component/Observable/ObservableStringComponent';
import CLog from '../Utility/ComponentLogger';
import IUserMessageManager from './IUserMessageManager';
import UserMessageNodeBuilder from './UserMessageNodeBuilder';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

@cc._decorator.ccclass
export default class UserMessageManager extends ReferencedComponent implements IUserMessageManager
{
    @property({ type: ObservableStringComponent, visible: true })
    private _observableUserMessage: ObservableStringComponent = null;
    public get ObservableUserMessage(): ObservableStringComponent { return this._observableUserMessage; }

    @property({ type: ObservableColorComponent, visible: true })
    private _observableUserMessageColor: ObservableColorComponent = null;
    public get ObservableUserMessageColor(): ObservableColorComponent { return this._observableUserMessageColor; }

    // /** The [[cc.Layout]] used by the user message display in the playbar. */
    // @property({ type: cc.Layout, visible: true })
    // private _layout: cc.Layout = null;
    // /** Accessor for the [[cc.Layout]] used by the user message display in the playbar. */
    // public get Layout(): cc.Layout { return this._layout; }
    // /** Accessor for the node that the [[cc.Layout]] is attached to. */
    // public get LayoutNode(): cc.Node { return this._layout.node; }

    // @property({ type: cc.Font, visible: true })
    // private _font: cc.Font = null;
    // public get Font(): cc.Font { return this._font; }

    // /** Font size to be used on the user message display. */
    // @property({ type: cc.Integer, visible: true })
    // private _fontSize: number = 20;
    // /** Accessor for the font size. */
    // public get FontSize(): number { return this._fontSize; }

    // @property({ type: cc.Integer, visible: true })
    // private _lineHeight: number = 42;
    // public get LineHeight(): number { return this._lineHeight; }

    // /** Width to use on in-line symbols in the message display. */
    // @property({ type: cc.Integer, visible: true })
    // private _symbolWidth: number = this._fontSize;
    // /** Accessor for the symbol width. */
    // public get SymbolWidth(): number { return this._symbolWidth; }

    // /** Spacing to use on in-line symbols in the message display. */
    // @property({ type: cc.Float, visible: true })
    // private _symbolSpacing: number = 1.5;
    // /** Accessor for the symbol spacing. */
    // public get SymbolSpacing(): number { return this._symbolSpacing; }

    // /** Should the display use the symbol's string value instead of its texture. */
    // @property({ visible: true })
    // private _useSymbolStringValue: boolean = false;
    // /** Accessor for use symbol string value. */
    // public get UseSymbolStringValue(): boolean { return this._useSymbolStringValue; }

    /** The delay to use when rotating between multiple user message displays. */
    @property({ type: cc.Integer, visible: true })
    private _rotateDelay: number = 1;

    /** The current rotate delay in use. */
    private _currentRotateDelay: number = this._rotateDelay;

    private _symbolSetRef: InjectableComponent<SymbolSet> = new InjectableComponent<SymbolSet>();
    /** Accessor for the current symbol set being used. */
    public get SymbolSet(): SymbolSet { return this._symbolSetRef.Value; }

    /** The builder being used to construct user message display values. */
    private _currentNodeBuilder: UserMessageNodeBuilder  = null;
    /** Accessor for the current [[UserMessageNodeBuilder]] in use. */
    public get CurrentNodeBuilder(): UserMessageNodeBuilder { return this._currentNodeBuilder; }
    /** Mutator to change the current [[UserMessageNodeBuilder]] to use. */
    public set CurrentNodeBuilder(value: UserMessageNodeBuilder)
    {
        this._currentRotateDelay = this._rotateDelay;
        this._currentNodeBuilder = value;
    }

    /** Is the builder in progress of creating a new user message display. */
    public BuildInProgress: boolean = false;

    /** @hidden */
    protected setContexts(): void
    {
        this._symbolSetRef.SetContext(SymbolSet);
    }

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        if (!this._observableUserMessage)
        {
            this.enabled = false;
            CLog.error("No observable component configured.", this);
        }
    }

    /** @hidden */
    protected update(dt: number): void
    {
        super.update(dt);

        this.rotationHandler(dt);
    }

    /**
     * @returns A newly created [[UserMessageNodeBuilder]].
     */
    public GetViewBuilder(): UserMessageNodeBuilder
    {
        return new UserMessageNodeBuilder(this);
    }

    /**
     * Clears any currently queued or added messages in the manager.
     * @returns This [[UserMessageManager]].
     */
    public Clear(): UserMessageManager
    {
        if (this.CurrentNodeBuilder)
        {
            this.CurrentNodeBuilder.Clear();
        }

        return this;
    }

    /**
     * Helper handler for rotating the displayed messages in and out.
     * @param dt Delta time.
     * @hidden
     */
    private rotationHandler(dt: number): void
    {
        if (!this.CurrentNodeBuilder) { return; }

        this._currentRotateDelay -= dt;

        if (this._currentRotateDelay < 0)
        {
            this.CurrentNodeBuilder.Rotate();
            this._currentRotateDelay = this._rotateDelay;
            return;
        }
    }
}
